import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";

export interface RunSegment {
  id: string;
  run_id: string;
  trail_segment_id: string;
  segment_order: number;
  equipment_type: string;
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
  distance_miles: number;
  obstacles_encountered: string[];
  conditions_reported?: string;
  difficulty_sentiment?: number | null;
  difficulty_comment?: string | null;
  created_at: string;
  // Joined data from trail_segments
  trail_segment?: {
    id: string;
    trail_name?: string;
    segment_description?: string;
    difficulty?: 'easy' | 'moderate' | 'hard' | 'extreme';
    surface_type?: string;
    length_miles: number;
  };
}

export const useRunSegments = (runId: string | undefined) => {
  const [segments, setSegments] = useState<RunSegment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRunSegments = async () => {
      if (!runId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error: fetchError } = await supabase
          .from('run_segments')
          .select(`
            *,
            trail_segment:trail_segments(
              id,
              trail_name,
              segment_description,
              difficulty,
              surface_type,
              length_miles
            )
          `)
          .eq('run_id', runId)
          .order('segment_order');

        if (fetchError) {
          console.error('Error fetching run segments:', fetchError);
          setError('Failed to load run segments');
          return;
        }

        setSegments(data || []);
      } catch (err) {
        console.error('Error fetching run segments:', err);
        setError('Failed to load run segments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRunSegments();
  }, [runId]);

  return { segments, isLoading, error };
};